import { IconButton, Stack, TextField, type SxProps, type Theme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

interface QuantityInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  size?: 'small' | 'medium';
  label?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

export function QuantityInput({
  value,
  onChange,
  min = 1,
  max = 999,
  size = 'small',
  label,
  disabled = false,
  sx,
}: QuantityInputProps) {
  const handleIncrement = () => {
    if (value < max) {
      onChange(value + 1);
    }
  };

  const handleDecrement = () => {
    if (value > min) {
      onChange(value - 1);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value) || min;
    onChange(Math.max(min, Math.min(max, newValue)));
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Select all text on focus for easy replacement
    e.target.select();
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={sx}>
      <IconButton
        size="small"
        onClick={handleDecrement}
        disabled={disabled || value <= min}
        sx={{
          p: 0.25,
          bgcolor: 'action.hover',
          '&:hover': { bgcolor: 'action.selected' },
        }}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
      <TextField
        size={size}
        type="number"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        disabled={disabled}
        label={label}
        inputProps={{ min, max, style: { textAlign: 'center' } }}
        sx={{ width: 60, '& input': { px: 0.5 } }}
      />
      <IconButton
        size="small"
        onClick={handleIncrement}
        disabled={disabled || value >= max}
        sx={{
          p: 0.25,
          bgcolor: 'action.hover',
          '&:hover': { bgcolor: 'action.selected' },
        }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}
